import _extends from "@babel/runtime/helpers/extends";
/** @jsx jsx */

import { memo, useRef } from 'react';
import { css, jsx } from '@emotion/react';
import { lazyForPaint, LazySuspense } from 'react-loosely-lazy';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { fontSize, gridSize } from '@atlaskit/theme/constants';
import Tooltip from '@atlaskit/tooltip';
import Step from './internal/step';
import useOverflowable from './internal/use-overflowable';
const gridSizeUnit = gridSize();
const height = gridSize() * 3 / fontSize();
const AKTooltip = lazyForPaint(() => import( /* webpackChunkName: "@atlaskit-internal_Tooltip" */'@atlaskit/tooltip'), {
  ssr: false
});
const itemWrapperStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  maxWidth: '100%',
  height: `${height}em`,
  margin: "var(--ds-space-0, 0px)",
  padding: "var(--ds-space-0, 0px)",
  flexDirection: 'row',
  lineHeight: `${height}em`,
  '&:not(:last-child)::after': {
    width: gridSizeUnit,
    padding: `${"var(--ds-space-0, 0px)"} ${"var(--ds-space-100, 8px)"}`,
    flexShrink: 0,
    content: '"/"',
    textAlign: 'center'
  }
});
const VAR_STEP_TRUNCATION_WIDTH = '--max-width';
const staticItemWithTruncationStyles = css({
  maxWidth: `var(${VAR_STEP_TRUNCATION_WIDTH})`,
  fontWeight: "var(--ds-font-weight-regular, 400)"
});
const staticItemWithoutTruncationStyles = css({
  minWidth: 0,
  flexShrink: 1,
  fontWeight: "var(--ds-font-weight-regular, 400)"
});
const BreadcrumbsItem = /*#__PURE__*/memo(props => {
  const buttonRef = useRef(null);
  const {
    truncationWidth,
    text,
    onTooltipShown,
    ...restButtonProps
  } = props;
  const showTooltip = useOverflowable(truncationWidth, buttonRef);
  const buttonProps = {
    ...restButtonProps,
    ref: buttonRef,
    hasOverflow: showTooltip
  };

  // Note: cast to `any` is required to type verification - see https://github.com/frenic/csstype#what-should-i-do-when-i-get-type-errors
  const dynamicItemStyles = {
    [VAR_STEP_TRUNCATION_WIDTH]: `${truncationWidth}px`
  };
  const step = jsx(Step, _extends({}, buttonProps, {
    css: truncationWidth ? staticItemWithTruncationStyles : staticItemWithoutTruncationStyles,
    style: dynamicItemStyles
  }), text);
  if (getBooleanFF('platform.design-system-team.remove-lazy-loading-of-tooltip-in-breadcrumbs_pki8p')) {
    return jsx("li", {
      css: itemWrapperStyles
    }, showTooltip ? jsx(Tooltip, {
      content: text,
      position: "bottom",
      onShow: onTooltipShown
    }, step) : step);
  }
  return jsx("li", {
    css: itemWrapperStyles
  }, showTooltip ? /* The div exists because of tooltip */
  jsx(LazySuspense, {
    fallback: jsx("div", null, step)
  }, jsx(AKTooltip, {
    content: text,
    position: "bottom",
    onShow: onTooltipShown
  }, step)) : step);
});

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default BreadcrumbsItem;